import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE, formatApiErrorDetail, formatCurrency } from "../lib/api";
import SignaturePad from "../components/app/SignaturePad";
import { CheckCircle, ShieldCheck, Download, ClipboardText, FileText, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PublicAllegatoA() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);

  // Form state
  const [dati, setDati] = useState({
    ragione_sociale: "", piva_cf: "", sede_legale: "", email: "", telefono: "", legale_rappresentante: "",
  });
  const [piano, setPiano] = useState("");
  const [confermaNoAss, setConfermaNoAss] = useState(false);
  const [datiSetup, setDatiSetup] = useState({ frigoriferi: "", aree_pulizia: "", utenti: "" });

  // Firma
  const [signerName, setSignerName] = useState("");
  const [firmaC, setFirmaC] = useState(null);
  const [firmaV, setFirmaV] = useState(null);
  const [acceptT, setAcceptT] = useState(false);
  const [acceptV, setAcceptV] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API_BASE}/public/allegati-a/${token}`);
      setData(d);
      // Precompila se ha già compilato
      if (d.cliente_compilato) setDati({ ...dati, ...d.cliente_compilato });
      else if (d.cliente_ragione_sociale) setDati((x) => ({ ...x, ragione_sociale: d.cliente_ragione_sociale, email: d.cliente_email || "" }));
      if (d.piano && d.piani[d.piano]) setPiano(d.piano);
      if (d.conferma_no_assistenza) setConfermaNoAss(true);
      if (d.dati_setup) setDatiSetup({ ...datiSetup, ...d.dati_setup });
      if (d.firmatario_cliente_nome) setSignerName(d.firmatario_cliente_nome);
      if (d.stato === "firmato") setStep(99);
    } catch {
      setData(false);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const pianoSel = data && piano ? data.piani[piano] : null;
  const promoSel = pianoSel ? data.promo?.[piano] : null;
  const totals = {
    canone: pianoSel ? (promoSel || pianoSel.yearly) : 0,
    listino: pianoSel ? pianoSel.yearly : 0,
    promo: !!promoSel,
    promo_nota: promoSel ? data.promo?.nota : null,
    canone_label: pianoSel ? `${pianoSel.label} · Annuale${promoSel ? " · PROMO" : ""}` : "Nessun piano selezionato",
  };

  async function saveCompilato() {
    // Validazioni base
    if (!dati.ragione_sociale.trim()) return toast.error("Ragione sociale obbligatoria");
    if (!dati.piva_cf.trim()) return toast.error("P.IVA / C.F. obbligatorio");
    if (!piano) return toast.error("Seleziona un piano");
    if (piano === "base" && !confermaNoAss) return toast.error("Per il Piano Base conferma che l'assistenza non è inclusa");
    setSubmitting(true);
    try {
      const { data: updated } = await axios.post(`${API_BASE}/public/allegati-a/${token}/compila`, {
        cliente_compilato: dati,
        piano,
        conferma_no_assistenza: piano === "base" && confermaNoAss,
        dati_setup: piano === "plus" ? datiSetup : null,
      });
      setData(updated);
      setStep(3);
      toast.success("Modulo compilato. Procedi con la firma.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore salvataggio");
    } finally { setSubmitting(false); }
  }

  async function submitFirma() {
    if (!signerName.trim()) return toast.error("Inserisci nome del firmatario");
    if (!firmaC) return toast.error("Disegna la firma principale");
    if (!firmaV) return toast.error("Disegna la firma per le clausole vessatorie");
    if (!acceptT || !acceptV) return toast.error("Accetta entrambe le condizioni");
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/public/allegati-a/${token}/firma`, {
        firmatario_cliente_nome: signerName.trim(),
        firma_cliente_data_url: firmaC,
        firma_clausole_data_url: firmaV,
        accept_terms: true,
      });
      toast.success("Modulo firmato. Verrà ricevuto dal Fornitore.");
      await load();
      setStep(99);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore firma");
    } finally { setSubmitting(false); }
  }

  if (loading) return <div className="min-h-screen bg-[#F4F5F7] flex items-center justify-center"><div className="text-sm text-[#64748B]">Caricamento…</div></div>;
  if (!data) return (
    <div className="min-h-screen bg-[#F4F5F7] flex items-center justify-center p-6">
      <div className="bg-white border border-[#E2E8F0] rounded-md p-10 max-w-md text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32F2F] mb-3">Errore 404</div>
        <h1 className="font-display text-2xl font-bold">Modulo non trovato</h1>
        <p className="text-sm text-[#64748B] mt-2">Il link potrebbe essere scaduto o non valido.</p>
      </div>
    </div>
  );

  const azienda = data.azienda?.ragione_sociale || "Sa.Gi Group";
  const isSigned = data.stato === "firmato" || step === 99;

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* Header */}
      <div className="bg-white border-b-2 border-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Allegato A · e-Log HACCP</div>
            <div className="font-display text-lg sm:text-xl font-bold">Modulo di scelta piano</div>
          </div>
          <div className="text-right text-xs">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Fornitore</div>
            <div className="font-bold">{azienda}</div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 space-y-6">
        {isSigned ? (
          <SignedBanner data={data} token={token} />
        ) : (
          <>
            <Stepper step={step} setStep={setStep} hasCompiled={data.stato === "compilato"} />

            {step === 1 && (
              <Section title="Dati cliente" eyebrow="Step 1 di 3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Inp label="Ragione sociale *" value={dati.ragione_sociale} onChange={(v) => setDati({ ...dati, ragione_sociale: v })} testid="dati-rs" />
                  <Inp label="P.IVA / Codice Fiscale *" value={dati.piva_cf} onChange={(v) => setDati({ ...dati, piva_cf: v })} testid="dati-piva" />
                  <Inp label="Sede legale" value={dati.sede_legale} onChange={(v) => setDati({ ...dati, sede_legale: v })} full testid="dati-sede" />
                  <Inp label="Email" type="email" value={dati.email} onChange={(v) => setDati({ ...dati, email: v })} testid="dati-email" />
                  <Inp label="Telefono" value={dati.telefono} onChange={(v) => setDati({ ...dati, telefono: v })} testid="dati-tel" />
                  <Inp label="Legale rappresentante" value={dati.legale_rappresentante} onChange={(v) => setDati({ ...dati, legale_rappresentante: v })} full testid="dati-lr" />
                </div>
                <div className="flex justify-end mt-6">
                  <button onClick={() => setStep(2)} data-testid="next-step-1" className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm">Avanti →</button>
                </div>
              </Section>
            )}

            {step === 2 && (
              <>
                <Section title="Selezione pacchetto" eyebrow="Step 2 di 3 · Fatturazione annuale">
                  <div className="space-y-3">
                    {Object.entries(data.piani).map(([key, p]) => (
                      <PianoCard key={key} k={key} p={p} promo={data.promo?.[key]} nota={data.promo?.nota} rinnovoText={data.promo_rinnovo_text} selected={piano === key} onSelect={() => { setPiano(key); if (key !== "base") setConfermaNoAss(false); }} />
                    ))}
                  </div>
                  {piano === "base" && (
                    <label className="flex items-start gap-3 mt-5 p-4 bg-orange-50 border-l-4 border-orange-500 text-sm cursor-pointer" data-testid="conferma-no-assistenza-box">
                      <input type="checkbox" checked={confermaNoAss} onChange={(e) => setConfermaNoAss(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#0033A0] shrink-0" data-testid="conferma-no-assistenza" />
                      <span><strong>Obbligatorio ·</strong> {data.conferma_no_assistenza_text}</span>
                    </label>
                  )}
                </Section>

                {piano === "plus" && <Section title="Dati per la configurazione" eyebrow="Da inviarci entro 3 giorni dalla firma">
                  <Inp label="Nomi frigoriferi / abbattitori" value={datiSetup.frigoriferi} onChange={(v) => setDatiSetup({ ...datiSetup, frigoriferi: v })} testid="ds-frigo" />
                  <Inp label="Aree di pulizia" value={datiSetup.aree_pulizia} onChange={(v) => setDatiSetup({ ...datiSetup, aree_pulizia: v })} testid="ds-aree" />
                  <Inp label="Nominativi utenti" value={datiSetup.utenti} onChange={(v) => setDatiSetup({ ...datiSetup, utenti: v })} testid="ds-utenti" />
                </Section>}

                <RiepilogoBox totals={totals} />

                <div className="flex justify-between gap-2">
                  <button onClick={() => setStep(1)} className="px-4 py-3 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm">← Indietro</button>
                  <button onClick={saveCompilato} disabled={submitting} data-testid="save-compilato" className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm">{submitting ? "Salvataggio…" : "Salva e Procedi alla Firma →"}</button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <Section title="Riepilogo contratto" eyebrow="Step 3 di 3 · Solo licenza software">
                  <p className="text-xs text-[#64748B] mb-4">
                    Riassunto degli articoli del contratto SaaS. <strong>Firmando l&apos;Allegato A qui sotto, il Cliente sottoscrive integralmente il Contratto SaaS &apos;HACCP e-LOG&apos; di cui il presente Modulo costituisce parte integrante e sostanziale.</strong> Il PDF integrale sarà scaricabile subito dopo la firma.
                  </p>
                  <div className="space-y-2 max-h-80 overflow-y-auto pr-2 border border-[#E2E8F0] rounded-sm p-4 bg-[#FAFBFC]">
                    {(data.articoli_contratto || []).map(([titolo, testo]) => (
                      <div key={titolo} className="text-xs leading-relaxed pb-2 border-b border-[#F4F5F7] last:border-0">
                        <strong className="text-[#0033A0]">{titolo}</strong>
                        <p className="text-[#0A0A0A] mt-0.5">{testo}</p>
                      </div>
                    ))}
                  </div>
                </Section>

                <RiepilogoBox totals={totals} />

                {piano === "base" && (
                  <div className="bg-orange-50 border-l-4 border-orange-500 p-4 text-xs flex items-start gap-2" data-testid="conferma-no-assistenza-riepilogo">
                    <CheckCircle size={16} weight="fill" className="text-orange-600 shrink-0 mt-0.5" />
                    <span>{data.conferma_no_assistenza_text}</span>
                  </div>
                )}

                <Section title="Firma del Cliente" eyebrow="Firma principale">
                  <Inp label="Nome e Cognome del firmatario *" value={signerName} onChange={setSignerName} testid="firma-name" full />
                  <div className="mt-3">
                    <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Firma *</label>
                    <SignaturePad onChange={setFirmaC} height={180} />
                  </div>
                  <label className="flex items-start gap-3 mt-3 text-xs cursor-pointer">
                    <input type="checkbox" checked={acceptT} onChange={(e) => setAcceptT(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#0033A0]" data-testid="accept-1" />
                    <span className="text-[#64748B]">Confermo di aver letto e accettato integralmente il contratto e l&apos;Allegato A. La firma apposta ha valore di firma elettronica semplice.</span>
                  </label>
                </Section>

                <Section title="Approvazione clausole vessatorie" eyebrow="ex artt. 1341 e 1342 c.c.">
                  <div className="text-xs leading-relaxed text-[#0A0A0A] mb-4 bg-orange-50 border-l-4 border-orange-500 p-3">
                    Approvo specificamente, mediante apposita seconda sottoscrizione, le seguenti clausole del contratto: <strong>Art. 5</strong> (Responsabilità e Limitazioni), <strong>Art. 7</strong> (Durata e Rinnovo Tacito), <strong>Art. 9</strong> (Sospensione per Morosità), <strong>Art. 10</strong> (Solve et Repete), <strong>Art. 11</strong> (Foro Competente Esclusivo).
                  </div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Seconda firma (clausole vessatorie) *</label>
                  <SignaturePad onChange={setFirmaV} height={150} />
                  <label className="flex items-start gap-3 mt-3 text-xs cursor-pointer">
                    <input type="checkbox" checked={acceptV} onChange={(e) => setAcceptV(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#0033A0]" data-testid="accept-2" />
                    <span className="text-[#64748B]">Confermo di approvare specificamente le clausole indicate, ai sensi degli artt. 1341 e 1342 del Codice Civile.</span>
                  </label>
                </Section>

                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                  <button onClick={() => setStep(2)} className="px-4 py-3 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm">← Indietro</button>
                  <div className="flex items-center gap-2 text-xs text-[#64748B]">
                    <ShieldCheck size={16} weight="duotone" className="text-[#008A3C]" />
                    Firma sicura tramite Gestionale 360
                  </div>
                  <button onClick={submitFirma} disabled={submitting} data-testid="submit-firma" className="btn-primary px-6 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm">{submitting ? "Firma in corso…" : "Firma e Invia →"}</button>
                </div>
              </>
            )}
          </>
        )}

        <div className="text-center text-[10px] uppercase tracking-[0.3em] text-[#64748B] pt-4">Powered by Gestionale 360</div>
      </div>
    </div>
  );
}

function Stepper({ step, setStep, hasCompiled }) {
  const steps = [
    { n: 1, label: "Dati cliente" },
    { n: 2, label: "Piano" },
    { n: 3, label: "Firma" },
  ];
  return (
    <div className="flex items-center gap-2 mb-4">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <button
            onClick={() => {
              if (s.n === 1) setStep(1);
              if (s.n === 2 && step >= 1) setStep(2);
              if (s.n === 3 && hasCompiled) setStep(3);
            }}
            className={`flex items-center gap-2 ${step >= s.n ? "text-[#0033A0]" : "text-[#64748B]"}`}
          >
            <span className={`w-7 h-7 inline-flex items-center justify-center text-xs font-bold rounded-sm ${step >= s.n ? "bg-[#0033A0] text-white" : "bg-[#E2E8F0] text-[#64748B]"}`}>{s.n}</span>
            <span className="text-xs uppercase tracking-[0.15em] font-bold hidden sm:inline">{s.label}</span>
          </button>
          {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${step > s.n ? "bg-[#0033A0]" : "bg-[#E2E8F0]"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function Section({ title, eyebrow, children }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-sm p-5 sm:p-7">
      {eyebrow && <div className="text-[10px] uppercase tracking-[0.3em] text-[#0033A0] mb-1 font-bold">{eyebrow}</div>}
      <h2 className="font-display text-xl sm:text-2xl font-bold mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Inp({ label, value, onChange, type = "text", full, testid }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-[#E2E8F0] rounded-sm focus:border-[#0033A0] focus:outline-none text-sm"
        data-testid={testid}
      />
    </div>
  );
}

function PianoCard({ k, p, promo, nota, rinnovoText, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`piano-${k}`}
      className={`w-full text-left p-4 border-2 rounded-sm transition-colors ${selected ? "border-[#0033A0] bg-blue-50" : "border-[#E2E8F0] hover:border-[#0A0A0A]"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-lg">{p.label}</div>
          {p.target && <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0033A0] mt-1">{p.target}</div>}
          <ul className="mt-3 space-y-1">
            {p.features.map((f) => (
              <li key={f} className={`text-xs leading-relaxed flex gap-2 ${!p.supporto && f.startsWith("Nessun supporto") ? "text-[#D32F2F] font-bold" : "text-[#64748B]"}`}>
                {!p.supporto && f.startsWith("Nessun supporto") ? <X size={14} weight="bold" className="shrink-0 mt-0.5" /> : <Check size={14} weight="bold" className="shrink-0 mt-0.5 text-[#008A3C]" />}{f}
              </li>
            ))}
          </ul>
        </div>
        <div className="text-right shrink-0">
          {promo ? (
            <>
              <div className="inline-block bg-[#D32F2F] text-white text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded-sm mb-1" data-testid={`piano-${k}-promo-badge`}>{nota || "Promo"}</div>
              <div className="font-mono text-sm text-[#64748B] line-through" data-testid={`piano-${k}-listino`}>€ {p.yearly.toFixed(0)}</div>
              <div className="font-mono font-bold text-xl text-[#D32F2F]" data-testid={`piano-${k}-prezzo`}>€ {Number(promo).toLocaleString("it-IT", { maximumFractionDigits: 2 })}</div>
              <div className="font-mono text-xs text-[#64748B]">/anno + IVA</div>
              <div className="text-[10px] text-[#008A3C] font-bold mt-1 max-w-[140px] ml-auto">{rinnovoText}</div>
            </>
          ) : (
            <>
              <div className="font-mono font-bold text-xl" data-testid={`piano-${k}-prezzo`}>€ {p.yearly.toFixed(0)}</div>
              <div className="font-mono text-xs text-[#64748B]">/anno + IVA</div>
            </>
          )}
        </div>
      </div>
    </button>
  );
}

function RiepilogoBox({ totals }) {
  return (
    <div className="bg-[#0A0A0A] text-white rounded-sm p-5 sm:p-6" data-testid="riepilogo-totali">
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1">Riepilogo</div>
      <h3 className="font-display text-xl font-bold mb-4">{totals.canone_label}</h3>
      {totals.promo && (
        <div className="flex justify-between text-sm mb-2" data-testid="riepilogo-listino">
          <span className="text-white/70">Prezzo di listino{totals.promo_nota ? ` · ${totals.promo_nota}` : ""}</span>
          <span className="font-mono line-through text-white/60">{formatCurrency(totals.listino)}</span>
        </div>
      )}
      <div className="flex justify-between pt-3 border-t-2 border-white text-lg font-bold">
        <span>CANONE ANNUALE</span>
        <span className="font-mono" data-testid="riepilogo-canone">{formatCurrency(totals.canone)}</span>
      </div>
      <div className="text-[10px] text-white/50 leading-relaxed pt-2">IVA esclusa · Fatturazione annuale anticipata.{totals.promo ? " Prezzo promozionale bloccato anche per i rinnovi successivi." : ""}</div>
    </div>
  );
}

function SignedBanner({ data, token }) {
  const totals = data.totals || {};
  return (
    <>
      <div className="bg-[#008A3C] text-white p-6 rounded-sm flex items-start gap-3" data-testid="signed-banner">
        <CheckCircle size={28} weight="fill" className="shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-bold text-base uppercase tracking-[0.15em]">Modulo firmato</div>
          <div className="text-sm mt-1 opacity-90">
            Firmato da <strong>{data.firmatario_cliente_nome}</strong>{data.signed_at && <> il {new Date(data.signed_at).toLocaleString("it-IT")}</>}.
            <br />I documenti firmati sono disponibili qui sotto.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <a
          href={`${API_BASE}/public/allegati-a/${token}/pdf`}
          target="_blank"
          rel="noreferrer"
          data-testid="download-modulo-btn"
          className="bg-white border-2 border-[#E2E8F0] p-4 rounded-sm hover:border-[#0033A0] flex items-start gap-3 transition-colors"
        >
          <FileText size={28} weight="duotone" className="text-[#0033A0] shrink-0 mt-1" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#64748B]">PDF</div>
            <div className="font-bold text-sm mt-0.5">Modulo Allegato A firmato</div>
            <div className="text-[11px] text-[#64748B] mt-1 flex items-center gap-1"><Download size={12} weight="bold" /> Scarica solo il modulo</div>
          </div>
        </a>
        <a
          href={`${API_BASE}/public/allegati-a/${token}/contratto`}
          target="_blank"
          rel="noreferrer"
          data-testid="download-contratto-btn"
          className="bg-[#0A0A0A] text-white p-4 rounded-sm hover:bg-[#0033A0] flex items-start gap-3 transition-colors"
        >
          <ClipboardText size={28} weight="duotone" className="text-white shrink-0 mt-1" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold opacity-70">CONTRATTO COMPLETO</div>
            <div className="font-bold text-sm mt-0.5">Contratto SaaS integrale + Allegato A</div>
            <div className="text-[11px] opacity-80 mt-1 flex items-center gap-1"><Download size={12} weight="bold" /> 12 articoli + modulo firmato</div>
          </div>
        </a>
      </div>
      <RiepilogoBox totals={totals} />
      <div className="bg-white border border-[#E2E8F0] rounded-sm p-5 text-xs leading-relaxed text-[#64748B]">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#0033A0] font-bold mb-2 flex items-center gap-2"><ClipboardText size={12} weight="bold" /> Prossimi passi</div>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Il Fornitore riceverà notifica della firma e ti contatterà a breve.</li>
          <li>Riceverai a parte la fattura di attivazione (pagamento anticipato).</li>
          <li>Dopo il saldo verrà avviata la configurazione del sistema e-Log per il tuo ristorante.</li>
        </ol>
      </div>
    </>
  );
}
